/**
 * Conexión de tiempo real.
 *
 * Regla del sistema, y conviene no olvidarla al construir las pantallas:
 * el socket AVISA, no es la fuente de la verdad. Un dispositivo que estuvo
 * desconectado dos minutos se perdió eventos, y en cocina eso son órdenes
 * que nunca aparecieron.
 *
 * Por eso cada pantalla recarga su lista por HTTP cuando llega `sincronizar`,
 * que el servidor emite al conectar y al reconectar. Los eventos sueltos sólo
 * sirven para actualizar en caliente mientras la conexión está viva.
 */
import { io, type Socket } from 'socket.io-client';
import { API_BASE } from './api';
import { sesion } from './sesion';

let socket: Socket | null = null;

export type EstadoConexion = 'conectando' | 'conectado' | 'desconectado';

const escuchasEstado = new Set<(estado: EstadoConexion) => void>();
let estadoActual: EstadoConexion = 'desconectado';

function fijarEstado(estado: EstadoConexion) {
  estadoActual = estado;
  for (const fn of escuchasEstado) fn(estado);
}

export function conectar(): Socket | null {
  if (!sesion.token) return null;
  if (socket?.connected) return socket;

  socket?.disconnect();
  fijarEstado('conectando');

  socket = io(API_BASE || window.location.origin, {
    auth: { token: sesion.token },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
  });

  socket.on('connect', () => fijarEstado('conectado'));
  socket.on('disconnect', () => fijarEstado('desconectado'));
  socket.on('connect_error', () => fijarEstado('desconectado'));

  return socket;
}

export function conectarSocketMesa(tokenMesa: string): Socket {
  if (socket?.connected) return socket;

  socket?.disconnect();
  fijarEstado('conectando');

  socket = io(API_BASE || window.location.origin, {
    auth: { tokenMesa },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
  });

  socket.on('connect', () => fijarEstado('conectado'));
  socket.on('disconnect', () => fijarEstado('desconectado'));
  socket.on('connect_error', () => fijarEstado('desconectado'));

  return socket;
}

export function desconectarSocketMesa() {
  desconectar();
}

export function desconectar() {
  socket?.disconnect();
  socket = null;
  fijarEstado('desconectado');
}

export function obtenerSocket() {
  return socket;
}

export function estadoConexion() {
  return estadoActual;
}

export function alCambiarConexion(fn: (estado: EstadoConexion) => void): () => void {
  escuchasEstado.add(fn);
  fn(estadoActual);
  return () => {
    escuchasEstado.delete(fn);
  };
}

/** Suscribe a un evento y devuelve la función para darse de baja. */
export function alRecibir<T>(evento: string, fn: (datos: T) => void) {
  const s = conectar();
  s?.on(evento, fn as (...args: unknown[]) => void);
  return () => {
    s?.off(evento, fn as (...args: unknown[]) => void);
  };
}
